import { useCallback, useEffect, useRef, useState } from "react";

interface UseTimerOptions {
	seconds?: number;
}

interface UseTimerReturn {
	timerLeft: number;
	timerActive: boolean;
	isExpired: boolean;
	startTimer: () => void;
	formatTimer: (seconds: number) => string;
}

export function useTimer({
	seconds = 300,
}: UseTimerOptions = {}): UseTimerReturn {
	const [timerLeft, setTimerLeft] = useState(seconds);
	const [timerActive, setTimerActive] = useState(false);
	// Explicit expiry flag: set true the moment the countdown hits 0 and held
	// until the next startTimer(). Deriving it from `timerActive && timerLeft===0`
	// never fired, because timerActive is cleared at the same tick (SHCT-341).
	const [isExpired, setIsExpired] = useState(false);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const startTimer = useCallback(() => {
		setTimerLeft(seconds);
		setTimerActive(true);
		setIsExpired(false);
		if (timerRef.current) clearInterval(timerRef.current);
		timerRef.current = setInterval(() => {
			setTimerLeft((prev) => {
				if (prev <= 1) {
					if (timerRef.current) clearInterval(timerRef.current);
					setTimerActive(false);
					setIsExpired(true);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [seconds]);

	useEffect(() => {
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, []);

	const formatTimer = (s: number) => {
		const m = String(Math.floor(s / 60)).padStart(2, "0");
		const sec = String(s % 60).padStart(2, "0");
		return `${m}:${sec}`;
	};

	return {
		timerLeft,
		timerActive,
		isExpired,
		startTimer,
		formatTimer,
	};
}
